import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Modal, Slide, Stack, Typography, Box, IconButton } from "@mui/material";
import ReactPerfectScrollbar from "react-perfect-scrollbar";

import { ReactComponent as SdIcon } from "../../../../../../../vendor/_new/icon/services/owasp.svg";
import { ReactComponent as CloseIcon } from "../../../../../../../vendor/_new/icon/navbar/notification/close_big.svg";
import { ReactComponent as InfoIcon } from "../../../../../../../vendor/_new/icon/sites/info.svg";

import { SafeTextField } from "../../../../../../common/Greens";

import colors from "../../../../../../../utils/colors";
import useWAFConfig from "../../../../../../../hooks/user/useWAFConfig";
import { CancelButton, SaveButton } from "../../../../../../common/Button";
import FormHelpTooltip from "../../../../../../common/FormHelpTooltip";
import { useNotification } from "../../../../../../../contexts/NotificationContext";
import { validateCustomBlockPageForm } from "../../../../../../../utils/validation";
import { useDrawerBaselineDirty, useGuardedDrawerClose } from "../../../../super/design";

const VALIDATION_FIELD_KEYS = {
  location: "locationUrl",
  waf: "wafUrl",
  interrupt: "interruptUrl",
  form: "form",
};

const ConfigCustomBlockPageSettingsModal = ({ siteUid, open, handleClose, blockPageToEdit, canConfigureWAFForSite = true }) => {
  const { configWafSetting } = useWAFConfig();
  const { showSuccess, showError } = useNotification();
  const [locationBlockPage, setLocationBlockPage] = useState(null);
  const [wafBlockPage, setWafBlockPage] = useState(null);
  const [interruptBlockPage, setInterruptBlockPage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [baselineKey, setBaselineKey] = useState(0);

  const editType = blockPageToEdit?.id ?? null;

  const formValidation = useMemo(
    () =>
      validateCustomBlockPageForm({
        locationUrl: locationBlockPage?.url,
        wafUrl: wafBlockPage?.url,
        interruptUrl: interruptBlockPage?.url,
        editType,
      }),
    [locationBlockPage?.url, wafBlockPage?.url, interruptBlockPage?.url, editType],
  );

  const touchField = useCallback((field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  }, []);

  const showFieldErr = useCallback(
    (field) => {
      const validationKey = VALIDATION_FIELD_KEYS[field] ?? field;
      return (submitAttempted || touched[field]) && formValidation.errors[validationKey];
    },
    [submitAttempted, touched, formValidation.errors],
  );

  const canSubmit = canConfigureWAFForSite;

  useEffect(() => {
    if (open) {
      setLocationBlockPage(blockPageToEdit?.id === "location" ? blockPageToEdit : null);
      setWafBlockPage(blockPageToEdit?.id === "waf" ? blockPageToEdit : null);
      setInterruptBlockPage(blockPageToEdit?.id === "interrupt" ? blockPageToEdit : null);
      setTouched({});
      setSubmitAttempted(false);
      setBaselineKey((k) => k + 1);
    }
  }, [blockPageToEdit, open]);

  const formSnapshot = useMemo(
    () => ({
      location: locationBlockPage,
      waf: wafBlockPage,
      interrupt: interruptBlockPage,
    }),
    [locationBlockPage, wafBlockPage, interruptBlockPage],
  );
  const isDirty = useDrawerBaselineDirty(open, formSnapshot, baselineKey);
  const { requestClose, confirmDialog } = useGuardedDrawerClose(handleClose, isDirty, {
    disabled: loading,
  });

  const buildBlockPagePayload = (pageState, existing) => {
    const url = String(pageState?.url ?? "").trim();
    if (existing) {
      return {
        url,
        enabled: pageState?.enabled ?? existing?.enabled ?? true,
      };
    }
    if (!url) return null;
    return { url, enabled: true };
  };

  const handleSave = async () => {
    if (!canConfigureWAFForSite) return;
    setSubmitAttempted(true);
    const validation = validateCustomBlockPageForm({
      locationUrl: locationBlockPage?.url,
      wafUrl: wafBlockPage?.url,
      interruptUrl: interruptBlockPage?.url,
      editType,
    });
    if (!validation.valid) {
      const firstError = Object.values(validation.errors).find(Boolean);
      if (firstError) showError(firstError);
      return;
    }

    const data = {};
    if (blockPageToEdit) {
      if (blockPageToEdit?.id === "location") data.location = buildBlockPagePayload(locationBlockPage, blockPageToEdit);
      if (blockPageToEdit?.id === "waf") data.waf = buildBlockPagePayload(wafBlockPage, blockPageToEdit);
      if (blockPageToEdit?.id === "interrupt") data.interrupt = buildBlockPagePayload(interruptBlockPage, blockPageToEdit);
    } else {
      const location = buildBlockPagePayload(locationBlockPage);
      const waf = buildBlockPagePayload(wafBlockPage);
      const interrupt = buildBlockPagePayload(interruptBlockPage);
      if (location) data.location = location;
      if (waf) data.waf = waf;
      if (interrupt) data.interrupt = interrupt;
    }
    setLoading(true);
    try {
      await configWafSetting(siteUid, "set_block_page", data);
      showSuccess("Custom block page settings saved successfully");
      handleClose();
    } catch (err) {
      console.error(err);
      showError(err?.message || "Failed to save custom block page settings");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <Modal 
      open={open} 
      onClose={requestClose}
      slotProps={{
        backdrop: {
          sx: { backgroundColor: 'rgba(0, 0, 0, 0.5)' }
        }
      }}
    >
      <Slide direction="left" in={open} mountOnEnter unmountOnExit>
        <Stack
          sx={{
            position: "fixed",
            top: 0,
            right: 0,
            width: "33%",
            minWidth: 480,
            height: "100vh",
            backgroundColor: "#fff",
            boxShadow: "-4px 0 20px rgba(0,0,0,0.1)",
            zIndex: 1300,
            borderTopLeftRadius: "8px",
          }}  
        >
          <Stack direction={"row"} justifyContent={"space-between"} spacing={6} sx={{ px: 4, pt: 4, pb: 3 }}>
          <Stack direction="row" alignItems="center" spacing={2}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "8px",
                  backgroundColor: "#EEF0FE",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <SdIcon style={{ width: 32, height: 32, color: "#6371F7" }} />
              </Box>
              <Stack>
                <Stack direction="row" alignItems="center" sx={{ flexWrap: "nowrap" }}>
                  <Typography
                    sx={ { fontWeight: "medium", fontSize: 20, lineHeight: "30px", color: colors.neutral[1000] }}
                  >
                    {blockPageToEdit ? "Edit custom block page" : "Add custom block page"}
                  </Typography>
                  <FormHelpTooltip tooltip="Define a branded HTML block page shown when WAF blocks a visitor. Map geo, IP, or country rules to a custom response instead of the default error." />
                </Stack>
                <Typography sx={ { fontSize: 12, lineHeight: "18px", color: colors.neutral[700] }}>
                  Create a personalized block page for restricted access.
                </Typography>
              </Stack>
            </Stack>
            <IconButton onClick={requestClose} aria-label="Close" sx={{ p: 0 }}>
              <CloseIcon sx={{ color: colors.neutral[600] }} />
            </IconButton>
          </Stack>
          <Box sx={{ py: 4, pl: 4, height: `calc(100vh - 118px)` }}>
            <ReactPerfectScrollbar>
              <Stack justifyContent={"space-between"} spacing={4} sx={{ pr: 4, height: "100%" }}>
                <Stack spacing={4}>
                  {showFieldErr("form") && (
                    <Typography sx={{ fontSize: 12, color: colors.red?.[500] || "#d32f2f" }}>
                      {formValidation.errors.form}
                    </Typography>
                  )}
                  {(!blockPageToEdit || blockPageToEdit?.id === "location") && (
                    <Stack spacing={3} sx={{ p: 4, borderRadius: "8px", border: `solid 1px ${colors.neutral[200]}` }}>
                      <Stack spacing={1}>
                        <Typography
                          sx={ { fontSize: 16, lineHeight: "24px", fontWeight: "medium", color: colors.neutral[1000] }}
                        >
                          Geo Location/Country/IP Address Block
                        </Typography>
                        <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[800] }}>
                          Customize error page for blocked visitors based on location or IP address.
                        </Typography>
                      </Stack>
                      <Stack spacing={1}>
                        <Typography
                          sx={ { fontSize: 16, lineHeight: "24px", fontWeight: "medium", color: colors.neutral[1000] }}
                        >
                          Custom Page
                        </Typography>
                        <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[800] }}>
                          Build your custom page and host it online. Address of customized page: (required)
                        </Typography>
                      </Stack>
                      <SafeTextField
                        sanitize={false}
                        label={"Enter site"}
                        placeholder="https://example.com/block-page"
                        fullWidth
                        value={locationBlockPage?.url ?? ""}
                        error={Boolean(showFieldErr("location"))}
                        helperText={showFieldErr("location") || ""}
                        onBlur={() => touchField("location")}
                        onChange={(e) => {
                          setLocationBlockPage((prev) => {
                            if (prev) {
                              return { ...prev, url: e.target.value };
                            } else {
                              return { id: "location", description: "Location", enabled: true, url: e.target.value };
                            }
                          });
                        }}
                      />
                    </Stack>
                  )}
                  {(!blockPageToEdit || blockPageToEdit?.id === "waf") && (
                    <Stack spacing={3} sx={{ p: 4, borderRadius: "8px", border: `solid 1px ${colors.neutral[200]}` }}>
                      <Stack spacing={1}>
                        <Typography
                          sx={ { fontSize: 16, lineHeight: "24px", fontWeight: "medium", color: colors.neutral[1000] }}
                        >
                          WAF Custom Block Page
                        </Typography>
                        <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[800] }}>
                          Personalise WAF block page with branded information for enhanced user experience.
                        </Typography>
                      </Stack>
                      <Stack spacing={1}>
                        <Typography
                          sx={ { fontSize: 16, lineHeight: "24px", fontWeight: "medium", color: colors.neutral[1000] }}
                        >
                          Custom Page
                        </Typography>
                        <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[800] }}>
                          Build your custom page and host it online. Address of customized page: (required)
                        </Typography>
                      </Stack>
                      <SafeTextField
                        sanitize={false}
                        label={"Enter site"}
                        placeholder="https://example.com/block-page"
                        fullWidth
                        value={wafBlockPage?.url ?? ""}
                        error={Boolean(showFieldErr("waf"))}
                        helperText={showFieldErr("waf") || ""}
                        onBlur={() => touchField("waf")}
                        onChange={(e) => {
                          setWafBlockPage((prev) => {
                            if (prev) {
                              return { ...prev, url: e.target.value };
                            } else {
                              return { id: "waf", description: "WAF", enabled: true, url: e.target.value };
                            }
                          });
                        }}
                      />
                    </Stack>
                  )}
                  {(!blockPageToEdit || blockPageToEdit?.id === "interrupt") && (
                    <Stack spacing={3} sx={{ p: 4, borderRadius: "8px", border: `solid 1px ${colors.neutral[200]}` }}>
                      <Stack spacing={1}>
                        <Typography
                          sx={ { fontSize: 16, lineHeight: "24px", fontWeight: "medium", color: colors.neutral[1000] }}
                        >
                          Service Interruption Errors
                        </Typography>
                        <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[800] }}>
                          Inform users about service interruptions with customized error pages and relevant instructions.
                        </Typography>
                      </Stack>
                      <Stack spacing={1}>
                        <Typography
                          sx={ { fontSize: 16, lineHeight: "24px", fontWeight: "medium", color: colors.neutral[1000] }}
                        >
                          Custom Page
                        </Typography>
                        <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[800] }}>
                          Build your custom page and host it online. Address of customized page: (required)
                        </Typography>
                      </Stack>
                      <SafeTextField
                        sanitize={false}
                        label={"Enter site"}
                        placeholder="https://example.com/block-page"
                        fullWidth
                        value={interruptBlockPage?.url ?? ""}
                        error={Boolean(showFieldErr("interrupt"))}
                        helperText={showFieldErr("interrupt") || ""}
                        onBlur={() => touchField("interrupt")}
                        onChange={(e) => {
                          setInterruptBlockPage((prev) => {
                            if (prev) {
                              return { ...prev, url: e.target.value };
                            } else {
                              return { id: "interrupt", description: "Interruption", enabled: true, url: e.target.value };
                            }
                          });
                        }}
                      />
                    </Stack>
                  )}
                  <Stack
                    direction={"row"}
                    spacing={2}
                    sx={{
                      minHeight: 48,
                      p: 2,
                      borderRadius: "8px",
                      backgroundColor: colors.primary["alpha"],
                      border: `solid 1px ${colors.primary[300]}`,
                    }}
                  >
                    <Box sx={{ width: 24, height: 24 }}>
                      <InfoIcon style={{ color: colors.primary[300], width: 24, height: 24 }} />
                    </Box>
                    <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.primary[300] }}>
                      You can update, review, or manage settings on this screen. Changes will be applied instantly after save!
                    </Typography>
                  </Stack>
                </Stack>
                <Stack direction={"row"} alignItems={"center"} spacing={3}>
                  <SaveButton onClick={handleSave} disabled={loading || !canSubmit} loading={loading} />
                  <CancelButton onClick={requestClose} />
                </Stack>
              </Stack>
            </ReactPerfectScrollbar>
          </Box>
        </Stack>
      </Slide>
    </Modal>
    {confirmDialog}
    </>
  );
};
export default ConfigCustomBlockPageSettingsModal;
